import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { 
  TrendingUp, 
  User, 
  Settings, 
  LogOut, 
  Menu,
  X,
  BarChart3
} from 'lucide-react';
import { useState } from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { toast } from 'sonner';

const Layout = ({ children }) => {
  const { user, isAdmin, isPremium, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/');
  };

  const navLinks = [
    { path: '/signals', label: 'Signals', icon: BarChart3 },
    ...(isAdmin ? [{ path: '/admin', label: 'Admin', icon: Settings }] : []),
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#050505]">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-[#1F1F1F] bg-black/80 backdrop-blur-xl">
        <div className="max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo */}
          <Link to="/signals" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#00FF94] rounded flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-black" strokeWidth={2.5} />
            </div>
            <span className="font-bold text-lg text-white hidden sm:block" style={{ fontFamily: 'Chivo, sans-serif' }}>
              CoinPulse
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {navLinks.map((link) => (
              <Link key={link.path} to={link.path}>
                <Button
                  variant="ghost"
                  className={`text-sm ${
                    isActive(link.path) 
                      ? 'text-[#00FF94] bg-[#00FF94]/10' 
                      : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  <link.icon className="w-4 h-4 mr-2" />
                  {link.label}
                </Button>
              </Link>
            ))}
          </nav>

          {/* User Menu */}
          <div className="flex items-center gap-3">
            {/* Premium Badge */}
            {isPremium && (
              <span className="hidden sm:inline-flex items-center gap-1.5 px-2 py-1 rounded-sm bg-[#6366F1]/10 border border-[#6366F1]/20 text-[#6366F1] text-xs font-mono uppercase tracking-wider">
                Premium
              </span>
            )}

            {/* User Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="ghost" 
                  className="text-[#A1A1AA] hover:text-white hover:bg-white/5"
                  data-testid="user-menu-btn"
                >
                  <div className="w-8 h-8 rounded-full bg-[#1F1F1F] flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <span className="ml-2 hidden sm:block text-sm truncate max-w-[120px]">
                    {user?.email}
                  </span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-[#0A0A0A] border-[#1F1F1F]">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-white truncate">{user?.email}</p>
                  <p className="text-xs text-[#737373]">
                    {isPremium ? 'Premium Account' : 'Free Account'}
                  </p>
                </div>
                <DropdownMenuSeparator className="bg-[#1F1F1F]" />
                <DropdownMenuItem 
                  onClick={() => navigate('/profile')}
                  className="cursor-pointer text-[#A1A1AA] hover:text-white focus:text-white focus:bg-white/5"
                  data-testid="dropdown-profile"
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#1F1F1F]" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="cursor-pointer text-[#FF0055] hover:text-[#FF0055] focus:text-[#FF0055] focus:bg-[#FF0055]/10"
                  data-testid="dropdown-logout"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden text-[#A1A1AA] hover:text-white"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#1F1F1F] bg-[#0A0A0A] px-4 py-3">
            <nav className="flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link 
                  key={link.path} 
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Button
                    variant="ghost"
                    className={`w-full justify-start text-sm ${
                      isActive(link.path) 
                        ? 'text-[#00FF94] bg-[#00FF94]/10' 
                        : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                    }`}
                  >
                    <link.icon className="w-4 h-4 mr-2" />
                    {link.label}
                  </Button>
                </Link>
              ))}
              <Link 
                to="/profile"
                onClick={() => setMobileMenuOpen(false)}
              >
                <Button
                  variant="ghost"
                  className={`w-full justify-start text-sm ${
                    isActive('/profile') 
                      ? 'text-[#00FF94] bg-[#00FF94]/10' 
                      : 'text-[#A1A1AA] hover:text-white hover:bg-white/5'
                  }`}
                >
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </Button>
              </Link>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="max-w-[1600px] mx-auto px-4 py-6">
        {children}
      </main>
    </div>
  );
};

export default Layout;
